import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Filter } from "lucide-react";

import { useClickOutside } from "components/ui/DataTable/hooks/useClickOutside";
import { useDropdownPosition } from "components/ui/DataTable/hooks/useDropdownPosition";
import { DatePickerField } from "components/ui/DatePickerField";

import styles from "./DataTableFilter.module.css";

const normalizeFiltersConfig = (config) => {
  if (!config?.length) return [];
  const first = config[0];
  if (first.options || first.type) {
    return config;
  }
  return [
    {
      id: "default",
      label: "",
      type: "multi",
      options: config.map(({ id, value, label, icon }) => ({
        value: value ?? id,
        label,
        icon,
      })),
    },
  ];
};

const getGroupValue = (filters, groupId, type) => {
  if (!filters) return type === "single" ? null : [];
  if (Array.isArray(filters)) {
    return groupId === "default" ? filters : [];
  }
  const val = filters[groupId];
  if (type === "single") return val ?? null;
  return Array.isArray(val) ? val : [];
};

export const DataTableFilter = ({ filters, filtersConfig = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const { position, updatePosition } = useDropdownPosition(buttonRef, isOpen);

  useClickOutside(isOpen, buttonRef, menuRef, () => setIsOpen(false));

  const groups = normalizeFiltersConfig(filtersConfig);
  if (!groups.length) return null;

  const totalOptions = groups.reduce((acc, g) => acc + (g.options?.length ?? 0), 0);
  const showSearch = totalOptions > 5;

  const filtersValue = filters?.value ?? filters ?? [];
  const handleChange = filters?.onChange ?? onChange;

  const handleMultiToggle = (groupId, optionValue) => {
    const current = getGroupValue(filtersValue, groupId, "multi");
    const next = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue];
    handleChange?.(groupId, next);
  };

  const handleSingleSelect = (groupId, optionValue) => {
    const current = getGroupValue(filtersValue, groupId, "single");
    const next = current === optionValue ? null : optionValue;
    handleChange?.(groupId, next);
  };

  const handleButtonClick = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
    if (isOpen) setSearch("");
  };

  return (
    <>
      <div className={styles.filterButtonWrapper}>
        <button
          ref={buttonRef}
          className={`${styles.filterButton} ${isOpen ? styles.active : ""}`}
          onClick={handleButtonClick}
        >
          Filter by
          <Filter size={18} className={styles.filterIcon} />
        </button>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.dropdown}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
            }}
          >
            {showSearch && (
              <div className={styles.searchWrapper}>
                <input
                  className={styles.searchInput}
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className={styles.dropdownContent}>
            {groups.map((group) => {
              if (group.type === "date") {
                return (
                  <div key={group.id} className={styles.filterGroup}>
                    <div className={styles.dateInputWrapper}>
                      <DatePickerField
                        label={group.label}
                        value={filtersValue?.[group.id] || ""}
                        onChange={(val) => handleChange?.(group.id, val)}
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div key={group.id} className={styles.filterGroup}>
                  {group.label && (
                    <p className={styles.groupHeader}>{group.label}</p>
                  )}
                  {group.options.filter((opt) =>
                    !search || opt.label.toLowerCase().includes(search.toLowerCase())
                  ).map((opt) => {
                    const isMulti = group.type !== "single";
                    const isSelected = isMulti
                      ? getGroupValue(filtersValue, group.id, "multi").includes(
                          opt.value
                        )
                      : getGroupValue(filtersValue, group.id, "single") ===
                        opt.value;
                    const Icon = opt.icon;

                    return (
                      <button
                        key={opt.value}
                        className={styles.dropdownItem}
                        onClick={() =>
                          isMulti
                            ? handleMultiToggle(group.id, opt.value)
                            : handleSingleSelect(group.id, opt.value)
                        }
                      >
                        {isMulti ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className={styles.checkbox}
                          />
                        ) : (
                          <input
                            type="radio"
                            checked={isSelected}
                            readOnly
                            className={styles.radio}
                            name={`filter-${group.id}`}
                          />
                        )}
                        {Icon && (
                          <span className={styles.optionIcon}>
                            <Icon size={16} />
                          </span>
                        )}
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
            </div>
            {(() => {
              const hasActive = groups.some((g) => {
                const val = filtersValue?.[g.id];
                if (g.type === "date") return !!val;
                if (g.type === "single") return val != null;
                return Array.isArray(val) ? val.length > 0 : false;
              });

              /*
               * Always rendered, disabled when there is nothing to clear. It used to be
               * hidden until a filter was set, which meant it was missing at the moment a
               * user goes looking for it, and the menu changed height as they picked
               * options. A control that is present and inert is easier to trust than one
               * that appears and disappears.
               */
              return (
                <div className={styles.clearWrapper}>
                  <button
                    className={styles.clearButton}
                    disabled={!hasActive}
                    onClick={() => {
                      if (!hasActive) return;
                      groups.forEach((g) => {
                        if (g.type === "date") handleChange?.(g.id, "");
                        else if (g.type === "multi") handleChange?.(g.id, []);
                        else handleChange?.(g.id, null);
                      });
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              );
            })()}
          </div>,
          document.body
        )}
    </>
  );
};
